<script setup lang="ts">
    import DisplaySequenceTag from '@/apps/ens/components/DisplaySequenceTag.vue';

    export type FormatChange = {
        icon: string;
        label: string;
    };

    defineProps<{
        description: string;
        displaySequenceNo: number | null;
        isCurrent: boolean;
        change: FormatChange | null;
        dropMarker?: 'before' | 'after' | null;
    }>();
</script>

<template>
    <div
        class="sort-row is-flex is-align-items-center"
        :class="{
            'has-text-weight-semibold': isCurrent,
            'drop-before': dropMarker === 'before',
            'drop-after': dropMarker === 'after'
        }"
        draggable="true"
        tabindex="0">
        <b-icon
            icon="drag"
            class="has-text-grey-light" />
        <display-sequence-tag :value="displaySequenceNo" />
        <span class="is-flex-grow-1 mr-3">
            {{ description || '(Empty description)' }}
            <b-tag
                v-if="isCurrent"
                type="is-info is-light"
                class="editing-tag">
                editing
            </b-tag>
        </span>
        <span class="change-label has-text-warning-dark">
            <span
                v-if="change"
                data-testid="change">
                <b-icon
                    :icon="change.icon"
                    size="is-small"
                    class="mr-1" />
                {{ change.label }}
            </span>
        </span>
    </div>
</template>

<style scoped>
.sort-row {
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--bulma-border-weak);
    border-radius: var(--bulma-radius);
    cursor: grab;
    user-select: none;
}

.sort-row:focus-visible {
    outline: 2px solid var(--eq-prominent);
    outline-offset: 1px;
}

/* A line in the middle of the gap before or after the row. --row-gap is set by the list the row is in,
   and the offsets start inside the row's 1px border. */
.sort-row.drop-before,
.sort-row.drop-after {
    position: relative;
}

.sort-row.drop-before::before,
.sort-row.drop-after::after {
    content: '';
    position: absolute;
    left: -1px;
    right: -1px;
    height: 3px;
    background: var(--eq-prominent);
}

.sort-row.drop-before::before {
    top: calc(-1px - var(--row-gap) / 2 - 1.5px);
}

.sort-row.drop-after::after {
    bottom: calc(-1px - var(--row-gap) / 2 - 1.5px);
}

.editing-tag {
    vertical-align: middle;
}

/* Fixed width so the change labels line up, with room for "changed (was 999)" */
.change-label {
    flex-shrink: 0;
    width: 10rem;
    white-space: nowrap;
}

/* Centres the icon on the lowercase letters; flex centring makes it look too high */
.change-label .icon {
    vertical-align: middle;
}
</style>
